import React, { useMemo, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { DataStore } from 'aws-amplify/datastore';
import { DNABox, DNAButton, DNADivider, DNAText } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
import { TenantLogoKey } from '@alucio/aws-beacon-amplify/src/API';
import { CustomFieldUsage, QuickFilter, Tenant } from '@alucio/aws-beacon-amplify/src/models';
import ActiveUser from 'src/state/global/ActiveUser';
import { TenantORM, useTenant, useTenantCustomFields } from 'src/state/redux/selector/tenant';
import { formatDataStorePayload } from 'src/state/redux/slice/common';
import UploadImageTenant, { TENANT_LOGO_VARIANCE } from './UploadImageTenant';

import { generateClient } from 'aws-amplify/api';
import { updateTenantLogo } from '@alucio/aws-beacon-amplify/src/graphql/mutations';
import omit from 'lodash/omit';

const styles = StyleSheet.create({
  actionsContainer: {
    paddingVertical: 20,
    paddingHorizontal: 16,
  },
  mainWrapper: {
    backgroundColor: colors['color-text-white'],
    overflowX: 'auto',
  },
});

interface ConfigurationProps {
  tenantId: string;
}

function removeInternalFields(tenantORM: TenantORM) {
  const tenant = { ...tenantORM.tenant };
  const internalFields = [
    'id', '_deleted', '_version', '_lastChangedAt', 'createdAt', 'createdBy',
    'updatedAt', 'updatedBy', 'statusChangedAt', 'statusChangedBy',
  ];
  return omit(tenant, internalFields);
}

const Configuration = (props: ConfigurationProps) => {
  const tenantORM = useTenant(props.tenantId);
  const jsonTenant = useMemo(() =>
    JSON.stringify(removeInternalFields(tenantORM!), undefined, 2),
  [tenantORM]);
  const [jsonConfig, setJsonConfig] = useState<string>(jsonTenant);
  const [error, setError] = useState<undefined | string>(undefined);
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const logos = useRef<Map<TENANT_LOGO_VARIANCE, TenantLogoKey>>(new Map());
  const tenantFields = useTenantCustomFields({
    tenantId: props.tenantId, usages: { internalUsages: [CustomFieldUsage.DOCUMENT] },
  });

  const validateQuickFilters = (quickFilters: QuickFilter[]) => {
    for (let i = 0; i < quickFilters.length; i++) {
      const filter = quickFilters[i];
      if (!filter.id || !filter.label || !filter.path) {
        setError('QuickFilter Error: A QuickFilter must contain an id, label, and path')
        return false;
      }
      for (let j = 0; j < filter.path.length; j++) {
        if (filter.path.some((id) => {
          const field = tenantFields.find((field) => field.id === id);
          if (!field) {
            setError(`QuickFilter Error: custom field id: ${id} is not a valid id for path: ${filter.label}`)
            return true
          }
          else if (!(field.fieldType === 'MULTICATEGORICAL' || field.fieldType === 'CATEGORICAL')) {
            // eslint-disable-next-line max-len
            setError(`QuickFilter Error: custom field id: ${id}. ${field.fieldLabel} is not categorical nor multicategorical`)
            return true
          }
        })) {
          return false
        }
      }
    }
    return true
  }

  async function save(): Promise<void> {
    try {
      setError(undefined);
      const jsonParsed = JSON.parse(jsonConfig);
      const now = new Date().toISOString();

      try {
        if (jsonTenant !== jsonConfig) {
          let continueUpdate = true;
          // validate quickFilters
          if (jsonParsed.config.quickFilters && jsonParsed.config.quickFilters.length) {
            const isValidFilters = validateQuickFilters(jsonParsed.config.quickFilters);
            if (!isValidFilters) {
              continueUpdate = false;
            }
          }

          if (continueUpdate) {
            const newStatus = jsonParsed.status !== tenantORM?.tenant.status;
            const payload = {
              ...jsonParsed,
              id: tenantORM?.tenant.id,
              updatedAt: now,
              // @ts-expect-error - This is an internal appsync only field that isn't typed
              _version: tenantORM?.tenant._version,
              statusChangedAt: newStatus ? now : tenantORM?.tenant.statusChangedAt,
              statusChangedBy: newStatus ? ActiveUser.user!.id : tenantORM?.tenant.statusChangedBy,
            };
            await DataStore.save(formatDataStorePayload(Tenant, tenantORM?.tenant!, payload));
          }
        }

        if (logos.current.size) {
          const payload: TenantLogoKey[] = [];
          logos.current.forEach((logo) => payload.push(logo));
          const appsyncClient = generateClient();
          await appsyncClient.graphql({
            query: updateTenantLogo,
            variables: {
              input: {
                tenantId: tenantORM?.tenant.id!,
                keys: payload,
              },
            },
          });
          setIsDirty(false);
        }
      } catch (e) {
        setError('Invalid Tenant (definition error)');
      }
    } catch (e) {
      console.error(e);
      setError('Invalid JSON (syntax error)');
    }
  }

  function isFormDirty(initialInput: string, currentInput: string) {
    return initialInput !== currentInput
  }

  function onJSONChange(value): void {
    setIsDirty(isFormDirty(jsonTenant, value.target.value))
    setJsonConfig(value.target.value);
  }

  function onFinishHandler(file, variance: TENANT_LOGO_VARIANCE): void {
    logos.current.set(variance, { key: file.key, variance });
    setIsDirty(true);
  }

  function removeImageHandler(variance: TENANT_LOGO_VARIANCE): void {
    logos.current.set(variance, { deleteLogo: true, variance });
    setIsDirty(true);
  }

  const UploadPrimaryLogoTenantMemo = useMemo(() => (<UploadImageTenant
    tenantId={tenantORM?.tenant.id!}
    onFinishHandler={onFinishHandler}
    variance={TENANT_LOGO_VARIANCE.PRIMARY}
    removeImage={removeImageHandler}
  />), [tenantORM?.tenant.id]);

  const UploadHubLogoTenantMemo = useMemo(() => (<UploadImageTenant
    tenantId={tenantORM?.tenant.id!}
    onFinishHandler={onFinishHandler}
    variance={TENANT_LOGO_VARIANCE.HUB}
    removeImage={removeImageHandler}
  />), [tenantORM?.tenant.id]);

  return (
    <DNABox style={styles.mainWrapper} fill appearance="col">
      <DNABox spacing="sm" alignX="end" alignY="center" style={styles.actionsContainer}>
        <DNAButton
          onPress={save}
          disabled={!isDirty}
        >
          Save
        </DNAButton>
      </DNABox>
      <DNADivider />

      <DNABox spacing="md" style={{ marginHorizontal: 16, marginVertical: 32 }}>
        {UploadPrimaryLogoTenantMemo}
        {UploadHubLogoTenantMemo}
      </DNABox>

      <DNABox appearance="col" style={styles.actionsContainer} fill>
        {
          error &&
            <DNAText style={{ marginBottom: 10 }} status="danger">{error}</DNAText>
        }
        <textarea
          onChange={onJSONChange}
          placeholder="Add JSON code..."
          value={jsonConfig}
          style={{
            minHeight: 200,
            height: '100%',
            borderRadius: 4,
            borderColor: colors[error ? 'color-danger-500' : 'color-gray-100'],
          }}
        />
      </DNABox>
    </DNABox>
  );
};

Configuration.displayName = 'Configuration';

export default Configuration;
